import React from 'react';
import { Route, Routes, Navigate } from 'react-router-dom'; // Removed unnecessary Router import
import {
  AuthenticatedTemplate,
  MsalProvider,
  UnauthenticatedTemplate,
} from '@azure/msal-react';
import { ClientSideNavigation } from './client-side-navigation.js';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import MilestonesPage from '../../Projects/Components/Schedule/template/MilestonesPage';
import TemplatesPage from '../../Projects/Components/Schedule/template/TemplatesPage.tsx';
import TemplateEditorPage from '../../Projects/Components/Schedule/template/TemplateEditorPage.tsx';
import WorkflowHubPage from '../../Projects/Components/Schedule/template/WorkflowHubPage';
import NavBar from '../../navbar/NavBar';
import HomePage from '../../Home/HomePage';
import Timer from '../../Timer/Timer.js';
import TasksPage from '../../Tasks/TasksPage';
import GeneralMatters from '../../Matters/MatterDetails/GeneralMatters/GeneralMatters.tsx';
import AgentsPage from '../../Agents/AgentsPage.tsx';
import SEPTOrbitPage from '../../SEPTOrbit/SEPTOrbitPage';

import StakeholderDetails from '../../SEPTOrbit/StakeholderDetails/StakeholderDetails';
import useCurrentEmployee from '../hooks/useCurrentEmployee';
import NotificationsPage from '../../Notifications/NotificationsPage';
import MattersPage from '../../Matters/MattersPage';
import CreateMattersPage from '../../Matters/CreateMatters/CreateMattersPage';
import Files from '../../Matters/MatterDetails/Files/Files.tsx';
import MatterCreateTasksPage from '../../Matters/MatterDetails/MatterTasks/MatterTasks/CreateTasks/MatterCreateTasksPage.js';
import Profile from '../../Profile/ProfilePage.js';
import CreateAgentPage from '../../Agents/CreateAgent/CreateAgentPage';
import CreateChecklistPage from '../../Tasks/Checklist/CreateChecklist/CreateChecklistsPage';
import CreateMatterChecklistPage from '../../Matters/MatterDetails/MatterTasks/MatterChecklist/CreateChecklist/CreateMatterChecklistPage.js';
import AdditionalNotes from '../../Matters/MatterDetails/AdditionalNotes/AdditionalNotes';
import ContactsPage from '../../Matters/MatterDetails/GeneralMatters/Components/TasksTable/Components/ContactsPage/ContactsPage.tsx';
import Team from '../../Team/Team';
import FinancialPage from '../../Matters/MatterDetails/Financial/FinancialPage';
import Billing from '../../Matters/MatterDetails/Financial/Billing/Billing';
import Expenses from '../../Matters/MatterDetails/Financial/Expenses/Expenses';
import TransactionsTable from '../../Matters/MatterDetails/Financial/Transactions/TransactionsTable';

// Projects — the engineering create-project page is still its own flow (create-page merge pending).
// All engineering detail sub-pages now reuse the Matter components above, so the Project detail
// duplicates were removed and their sibling routes point at the shared Matter components.
import CreateProjectsPage from '../../Projects/CreateProjects/CreateProjectsPage.tsx';

import FinancialsPage from '../../Financial/FinancialsPage';
import DisbursementPage from '../../Matters/MatterDetails/Financial/Disbursements/DisbursementPage.js';
import MatterDetails from '../../Matters/MatterDetails/MatterDetails';
import ProjectDetailRouter from '../../Projects/ProjectDetailRouter';
import SiblingRouteGuard from '../../Projects/SiblingRouteGuard';
import CreateTasksPage from '../../Tasks/Tasks/CreateTasks/CreateTasksPage';
import InvoicePage from '../../Matters/MatterDetails/Financial/Invoice/InvoicePage';
import DisbursementTable from '../../Matters/MatterDetails/Financial/Disbursements/DisbursementPage.js';
import MatterTasksPage from '../../Matters/MatterDetails/MatterTasks/MatterTasksPage.tsx';
import GeneralInvoicesPage from '../../Financial/Invoice/GeneralInvoicePage.tsx';
import GeneralBillingPage from '../../Financial/Billing/GeneralBilling';
import GeneralExpenses from '../../Financial/Expenses/GeneralExpenses';
import GeneralTransactionsTable from '../../Financial/Transactions/GeneralTransactionsTable.tsx';
import GeneralDisbursementsPage from '../../Financial/Disbursements/GeneralDisbursementPage.js';

import ToastContainer from '../../Components/ToastContainer/ToastContainer';
import {
  isEngineeringEnvironment,
  isLegalEnvironment,
  isFeatureEnabled,
} from '@frontend/AppSettings/environmentConfig';
import BidSheetsPage from '../../BidSheets/BidSheetsPage';
import BidDetailPage from '../../BidSheets/BidDetail/BidDetailPage';
import BidAdminPage from '../../BidSheets/Admin/BidAdminPage';
import { useBidCapabilities } from '../../BidSheets/useBidCapabilities';
import RuntimeConfigLoader from './RuntimeConfigLoader';
import { useRuntimeConfig } from '../../__settings__/runtimeConfig';

// A1: CRM (SEPT Orbit) is hidden from permish-3 in the nav; guard the routes too so a direct URL
// doesn't render the CRM chrome for a standard user. The data/API half is already gated (A31), so
// this closes the remaining chrome leg of AUTH-N02. While the employee record resolves, render
// nothing (avoid a flash); a permish-3 (or NULL → 3) user is redirected to their Timesheets view.
function RequireCrmAccess({ children }) {
  const currentEmployee = useCurrentEmployee();
  if (currentEmployee === null) return null;
  const raw = currentEmployee?.permish_id;
  const permish = typeof raw === 'string' ? parseInt(raw, 10) : raw;
  const resolved =
    permish === null || permish === undefined || Number.isNaN(permish) ? 3 : permish;
  if (resolved === 3) return <Navigate to="/financials/billing" replace />;
  return children;
}

// Reference-data admin follows the BACKEND capability, not permish (P4). The catalog writes are
// gated on ADMIN_CATALOGS — held by a bid_administrator, or by a system admin via the retained
// superuser fallback — so asking the server "may I?" is the only way the screen and the API agree.
// Renders nothing while it resolves and denies on failure (useBidCapabilities fails closed), so a
// standard user never sees a flash of the admin screen.
function RequireBidAdmin({ children }) {
  const capabilities = useBidCapabilities();
  if (capabilities === null) return null;
  if (!capabilities.isBidAdmin) return <Navigate to="/bids" replace />;
  return children;
}

const queryClient = new QueryClient();

// // Projects
// // Designs

// import FinancialsPage from '../../Financials/FinancialsPage.js';
// import ContactsPage from '../../ContactsPage/ContactsPage.js';
// Create a Query Client

const App = ({ pca }) => {
  // Subscribe App itself to the runtime config (P1): the flag-gated <Route> entries
  // below call isFeatureEnabled() in this render body, so App must re-render when the
  // fetched config lands — otherwise the nav (which subscribes in NavBarAuth) and the
  // route set would disagree for the whole session.
  useRuntimeConfig();
  // E2E test seam: the app has no in-app login UI and gates everything behind
  // <AuthenticatedTemplate>. When REACT_APP_E2E==='true' we render the routed app
  // directly so Playwright can drive it without a real Azure AD sign-in. Guarded by
  // the flag, so this is completely inert in normal and production builds.
  const AuthGate =
    process.env.REACT_APP_E2E === 'true' ? React.Fragment : AuthenticatedTemplate;
  return (
    <QueryClientProvider client={queryClient}>
      <ClientSideNavigation pca={pca}>
        <MsalProvider instance={pca}>
          <RuntimeConfigLoader />
          <nav>
            <NavBar />
          </nav>
          <AuthGate>
            <div>
              <ToastContainer />
              <Routes>
                <Route path="/" element={<HomePage />} />
                <Route path="/workflow-hub/*" element={<WorkflowHubPage />} />
                <Route path="/milestones" element={<MilestonesPage />} />
                <Route path="/templates" element={<TemplatesPage />} />
                <Route path="/templates/:templateId/edit" element={<TemplateEditorPage />} />
                <Route path="/templates/create" element={<TemplateEditorPage />} />

                <Route path="/tasks" element={<TasksPage />} />
                <Route path="/sept-orbit" element={<RequireCrmAccess><SEPTOrbitPage /></RequireCrmAccess>} />
                <Route path="/stakeholder/:type/:stakeholderId" element={<RequireCrmAccess><StakeholderDetails /></RequireCrmAccess>} />
                <Route path="/notifications" element={<NotificationsPage />} />
                <Route path="/createTasks" element={<CreateTasksPage />} />
                <Route
                  path="/createChecklist"
                  element={<CreateChecklistPage />}
                />
                <Route path="/profile" element={<Profile />} />
                <Route path="/contacts" element={<ContactsPage />} />
                <Route path="/team" element={<Team />} />

                {/* Bids / estimating (feature-flagged) */}
                {isFeatureEnabled('bids') && (
                  <>
                    <Route path="/bids" element={<BidSheetsPage />} />
                    {/* before /bids/:bidId, or 'admin' is captured as a bid id */}
                    <Route
                      path="/bids/admin"
                      element={
                        <RequireBidAdmin>
                          <BidAdminPage />
                        </RequireBidAdmin>
                      }
                    />
                    <Route path="/bids/:bidId" element={<BidDetailPage />} />
                  </>
                )}

                {/* Legal-specific Routes */}
                {isLegalEnvironment && (
                  <>
                    <Route
                      path="/generalmatters"
                      element={<GeneralMatters />}
                    />
                    <Route path="/agents" element={<AgentsPage />} />
                    <Route path="/createAgent" element={<CreateAgentPage />} />
                    <Route path="/Timer" element={<Timer />} />

                    {/* Matter Routes */}
                    <Route path="/matters" element={<MattersPage />}></Route>
                    <Route
                      path="/createMatters"
                      element={<CreateMattersPage />}
                    ></Route>
                    <Route
                      path="/matterDetails/:matterId"
                      element={<MatterDetails />}
                    ></Route>
                    <Route
                      path="/matterDetails/:matterId/files"
                      element={<Files />}
                    ></Route>
                    <Route
                      path="/matterDetails/:matterId/tasks"
                      element={<MatterTasksPage />}
                    ></Route>
                    <Route
                      path="/matterDetails/:matterId/tasks/createTasks"
                      element={<MatterCreateTasksPage />}
                    ></Route>
                    <Route
                      path="/matterDetails/:matterId/tasks/createChecklist"
                      element={<CreateMatterChecklistPage />}
                    ></Route>
                    <Route
                      path="/matterDetails/:matterId/financial/*"
                      element={<FinancialPage />}
                    ></Route>
                    <Route
                      path="/matterDetails/:matterId/disbursements"
                      element={<DisbursementPage />}
                    ></Route>
                    <Route
                      path="/matterDetails/:matterId/additionalNotes"
                      element={<AdditionalNotes />}
                    ></Route>
                  </>
                )}

                {/* Project Routes */}
                {isEngineeringEnvironment && (
                  <>
                    <Route path="/agents" element={<AgentsPage />} />

                    <Route path="/projects" element={<MattersPage />}></Route>
                    <Route
                      path="/createProjects"
                      element={<CreateProjectsPage />}
                    ></Route>
                    {/* Retired by the projects/matters consolidation (P3): the standalone
                        GeneralProjects screen is gone, its content lives in the project
                        detail. Redirect rather than 404 a bookmarked URL. */}
                    <Route
                      path="/generalProjects"
                      element={<Navigate to="/projects" replace />}
                    />
                    {/* MatterDetails is the front door (regular chrome or pipeline tabs).
                        ProjectDetailRouter is a thin alias of that same component. */}
                    <Route
                      path="/projectDetails/:matterId"
                      element={<ProjectDetailRouter />}
                    ></Route>
                    {/* Projects and matters are the same entity; these sibling detail pages reuse the
                        Matter components, which derive their in-page nav prefix from the environment
                        (/projectDetails here). SiblingRouteGuard still redirects converted bids to the
                        pipeline detail. */}
                    <Route
                      path="/projectDetails/:matterId/files"
                      element={<SiblingRouteGuard><Files /></SiblingRouteGuard>}
                    ></Route>
                    <Route
                      path="/projectDetails/:matterId/tasks"
                      element={<SiblingRouteGuard><MatterTasksPage /></SiblingRouteGuard>}
                    ></Route>
                    <Route
                      path="/projectDetails/:matterId/tasks/createTasks"
                      element={<SiblingRouteGuard><MatterCreateTasksPage /></SiblingRouteGuard>}
                    ></Route>
                    <Route
                      path="/projectDetails/:matterId/tasks/createChecklist"
                      element={<SiblingRouteGuard><CreateMatterChecklistPage /></SiblingRouteGuard>}
                    ></Route>
                    <Route
                      path="/projectDetails/:matterId/additionalNotes"
                      element={<SiblingRouteGuard><AdditionalNotes /></SiblingRouteGuard>}
                    ></Route>

                  </>
                )}

                {/* Financial Routes (shared across environments) */}
                {(isLegalEnvironment || isEngineeringEnvironment) && (
                  <>
                    <Route path="/financials/*" element={<FinancialsPage />} />
                    <Route path="/financial/invoice" element={<Navigate to="/financials/invoice" replace />} />
                    <Route path="/financial/billing" element={<Navigate to="/financials/billing" replace />} />
                    <Route path="/financial/expenses" element={<Navigate to="/financials/expenses" replace />} />
                    <Route path="/financial/transactions" element={<Navigate to="/financials/transactions" replace />} />
                    <Route path="financial/disbursements" element={<GeneralDisbursementsPage />} />
                  </>
                )}
              </Routes>
            </div>
          </AuthGate>
          <UnauthenticatedTemplate></UnauthenticatedTemplate>
        </MsalProvider>
      </ClientSideNavigation>
    </QueryClientProvider>
  );
};

export default App;
